import React, { useState, useEffect } from 'react'
import { Button, Form, Table } from 'react-bootstrap'
import {
    IFeed_Input_Rule,
    IFeed_Input_Rule_Column,
    IFeed_Input_Rule_Cell,
    IFeed_Input_Rule_Row,
    IFeed_Input_Rule_Column_Add,
    IFeed_Input_Rule_Column_Copy,
    IFeed_Input_Rule_Cell_Append,
    IFeed_Input_Rule_Cell_Replace,
    IFeed_Input_Rule_Cell_EnsureLowest
} from './types'
import { FaTrash } from 'react-icons/fa'

interface FeedRulesFormProps {
    rules: IFeed_Input_Rule[]
    onChange: (updatedRules: IFeed_Input_Rule[]) => void
}

const FeedRulesForm: React.FC<FeedRulesFormProps> = ({ rules, onChange }) => {
    const [rows, setRows] = useState<{ kind: 'column' | 'cell' | 'row' | ''; type: string; rule: IFeed_Input_Rule | null }[]>([])

    useEffect(() => {
        const initializedRows = rules.map(rule => ({
            kind: rule.kind,
            type: rule.type,
            rule,
        }))
        setRows(initializedRows)
    }, [rules])

    const handleAddRow = () => {
        setRows([...rows, { kind: '', type: '', rule: null }])
    }

    const handleRowKindChange = (index: number, kind: 'column' | 'cell' | 'row') => {
        const updatedRows = [...rows]
        updatedRows[index] = { kind, type: '', rule: null }
        setRows(updatedRows)
    }

    const handleRowTypeChange = (index: number, type: string) => {
        const kind = rows[index].kind
        let newRule: IFeed_Input_Rule | null = null

        switch (kind) {
            case 'column':
                newRule = {
                    kind: 'column',
                    type: type as 'add' | 'copy' | 'remove' | 'replace',
                    columnName: '',
                    ...(type === 'add' && { columnValue: '' }),
                    ...(type !== 'remove' && type !== 'add' && { newColumnName: '' }),
                } as IFeed_Input_Rule_Column
                break
            case 'cell':
                if (type === 'ensureLowest') {
                    newRule = {
                        kind: 'cell',
                        type: 'ensureLowest',
                        referenceColumn: '',
                        targetColumn: ''
                    } as IFeed_Input_Rule_Cell_EnsureLowest
                } else {
                    newRule = {
                        kind: 'cell',
                        type: type as 'append' | 'replace',
                        columnName: '',
                        ...(type === 'append' && { content: '' }),
                        ...(type === 'replace' && { fromValue: '', toValue: '', rule: '' }),
                    } as IFeed_Input_Rule_Cell
                }
                break
            case 'row':
                newRule = {
                    kind: 'row',
                    type: 'remove',
                    columnName: '',
                    fromValue: '',
                } as IFeed_Input_Rule_Row
                break
            default:
                return
        }

        const updatedRows = [...rows]
        updatedRows[index] = { ...updatedRows[index], type, rule: newRule }
        setRows(updatedRows)
        onChange(updatedRows.map(row => row.rule!).filter(rule => rule !== null))
    }

    const handleRuleChange = (index: number, field: string, value: any) => {
        const updatedRows = [...rows]
        const rule = updatedRows[index].rule

        if (rule) {
            updatedRows[index].rule = { ...rule, [field]: value }
            setRows(updatedRows)
            onChange(updatedRows.map(row => row.rule!).filter(rule => rule !== null))
        }
    }

    const handleRemoveRow = (index: number) => {
        const updatedRows = rows.filter((_, i) => i !== index)
        setRows(updatedRows)
        onChange(updatedRows.map(row => row.rule!).filter(rule => rule !== null))
    }

    const renderRuleFields = (rule: IFeed_Input_Rule, index: number) => {
        switch (rule.kind) {
            case 'column':
                return (
                    <>
                        <Form.Control
                            type="text"
                            placeholder="Column Name"
                            value={(rule as IFeed_Input_Rule_Column).columnName}
                            onChange={(e) => handleRuleChange(index, 'columnName', e.target.value)}
                            className="form-control form-control-sm"
                            required
                        />
                        {rule.type === 'add' && (
                            <Form.Control
                                type="text"
                                placeholder="Column Value"
                                value={(rule as IFeed_Input_Rule_Column_Add).columnValue || ''}
                                onChange={(e) => handleRuleChange(index, 'columnValue', e.target.value)}
                                className="form-control form-control-sm"
                                required
                            />
                        )}
                        {rule.type !== 'remove' && rule.type !== 'add' && (
                            <>
                                <Form.Control
                                    type="text"
                                    placeholder={rule.type === 'copyIfEmpty' ? 'Target column name' : 'New Column Name'}
                                    value={(rule as IFeed_Input_Rule_Column_Copy).newColumnName || ''}
                                    onChange={(e) => handleRuleChange(index, 'newColumnName', e.target.value)}
                                    className="form-control form-control-sm"
                                    required={rule.type === 'copy' || rule.type === 'replace'}
                                />
                            </>
                        )}
                    </>
                )
            case 'cell':
                if (rule.type === 'ensureLowest') {
                    return (
                        <>
                            <Form.Control
                                type="text"
                                placeholder="Reference Column"
                                value={(rule as IFeed_Input_Rule_Cell_EnsureLowest).referenceColumn}
                                onChange={(e) => handleRuleChange(index, 'referenceColumn', e.target.value)}
                                className="form-control form-control-sm"
                                required
                            />
                            <Form.Control
                                type="text"
                                placeholder="Target Column"
                                value={(rule as IFeed_Input_Rule_Cell_EnsureLowest).targetColumn}
                                onChange={(e) => handleRuleChange(index, 'targetColumn', e.target.value)}
                                className="form-control form-control-sm"
                                required
                            />
                        </>
                    )
                } else {
                    return (
                        <>
                            <Form.Control
                                type="text"
                                placeholder="Column Name"
                                value={(rule as IFeed_Input_Rule_Cell_Append | IFeed_Input_Rule_Cell_Replace).columnName}
                                onChange={(e) => handleRuleChange(index, 'columnName', e.target.value)}
                                className="form-control form-control-sm"
                                required
                            />
                            {rule.type === 'append' && (
                                <Form.Control
                                    type="text"
                                    placeholder="Content"
                                    value={(rule as IFeed_Input_Rule_Cell_Append).content}
                                    onChange={(e) => handleRuleChange(index, 'content', e.target.value)}
                                    className="form-control form-control-sm"
                                    required
                                />
                            )}
                            {rule.type === 'replace' && (
                                <>
                                    <Form.Control
                                        type="text"
                                        placeholder="From Value"
                                        value={(rule as IFeed_Input_Rule_Cell_Replace).fromValue}
                                        onChange={(e) => handleRuleChange(index, 'fromValue', e.target.value)}
                                        className="form-control form-control-sm"
                                        required
                                    />
                                    <Form.Control
                                        type="text"
                                        placeholder="To Value"
                                        value={(rule as IFeed_Input_Rule_Cell_Replace).toValue}
                                        onChange={(e) => handleRuleChange(index, 'toValue', e.target.value)}
                                        className="form-control form-control-sm"
                                        required
                                    />
                                    <Form.Control
                                        as="select"
                                        value={(rule as IFeed_Input_Rule_Cell_Replace).rule || ''}
                                        onChange={(e) => handleRuleChange(index, 'rule', e.target.value)}
                                        className="form-control form-control-sm"
                                        required={false}
                                    >
                                        <option value="">No rule</option>
                                        <option value="decode">Decode</option>
                                        <option value="encode">Encode</option>
                                    </Form.Control>
                                </>
                            )}
                        </>
                    )
                }
            case 'row':
                return (
                    <>
                        <Form.Control
                            type="text"
                            placeholder="Column Name"
                            value={(rule as IFeed_Input_Rule_Row).columnName}
                            onChange={(e) => handleRuleChange(index, 'columnName', e.target.value)}
                            className="form-control form-control-sm"
                            required
                        />
                        <Form.Control
                            type="text"
                            placeholder="From Value"
                            value={(rule as IFeed_Input_Rule_Row).fromValue}
                            onChange={(e) => handleRuleChange(index, 'fromValue', e.target.value)}
                            className="form-control form-control-sm"
                            required
                        />
                    </>
                )
            default:
                return null
        }
    }

    return (
        <div className='mt-3'>
            <Form.Label>Rules</Form.Label>
            <Table size="sm" bordered hover>
                <thead>
                    <tr>
                        <th>Kind</th>
                        <th>Type</th>
                        <th>Details</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index}>
                            <td>
                                <Form.Control
                                    as="select"
                                    value={row.kind}
                                    onChange={(e) => handleRowKindChange(index, e.target.value as 'column' | 'cell' | 'row')}
                                    className="form-control form-control-sm"
                                    required
                                >
                                    <option value="">Select Kind</option>
                                    <option value="column">Column</option>
                                    <option value="cell">Cell</option>
                                    <option value="row">Row</option>
                                </Form.Control>
                            </td>
                            <td>
                                {row.kind && (
                                    <Form.Control
                                        as="select"
                                        value={row.type}
                                        onChange={(e) => handleRowTypeChange(index, e.target.value)}
                                        className="form-control form-control-sm"
                                        required
                                    >
                                        <option value="">Select Type</option>
                                        {row.kind === 'column' && (
                                            <>
                                                <option value="add">Add</option>
                                                <option value="copy">Copy</option>
                                                <option value="copyIfEmpty">Copy if empty</option>
                                                <option value="remove">Remove</option>
                                                <option value="replace">Replace</option>
                                            </>
                                        )}
                                        {row.kind === 'cell' && (
                                            <>
                                                <option value="append">Append</option>
                                                <option value="replace">Replace</option>
                                                <option value="ensureLowest">Exchange Lowest Value</option>
                                            </>
                                        )}
                                        {row.kind === 'row' && (
                                            <option value="remove">Remove</option>
                                        )}
                                    </Form.Control>
                                )}
                            </td>
                            <td>
                                {row.rule && renderRuleFields(row.rule, index)}
                            </td>
                            <td className='text-center'>
                                <FaTrash
                                    onClick={() => handleRemoveRow(index)}
                                    style={{ color: 'red', cursor: 'pointer' }}
                                    className="react-icons"
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Button
                variant="primary"
                onClick={handleAddRow}
                className="btn-sm mb-3"
            >
                Add New Rule
            </Button>
        </div>
    )
}

export default FeedRulesForm
